import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Heart, ArrowUpRight } from 'lucide-react';
import { companyInfo } from '../mockData';
import Logo from './Logo';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900 text-white overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle at 20% 80%, white 2px, transparent 2px), radial-gradient(circle at 80% 20%, white 2px, transparent 2px)',
        backgroundSize: '60px 60px'
      }}></div>
      <div className="absolute top-20 right-10 w-64 h-64 bg-purple-500 rounded-full opacity-20 blur-3xl animate-float"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-pink-500 rounded-full opacity-20 blur-3xl animate-float" style={{animationDelay: '1s'}}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Creative Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-12">
          {/* Company Info - Spans More Columns */}
          <div className="lg:col-span-5">
            <div className="mb-6">
              <Logo textColor="text-white" />
            </div>
            <p className="text-purple-200 text-lg leading-relaxed mb-6">
              Your trusted partner for Print on Demand and fulfillment excellence. 
              Focus on growing your brand — we'll take care of the rest.
            </p>
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/20">
                <div className="text-2xl font-black text-white">99.2%</div>
                <div className="text-xs text-purple-200">On-time Delivery</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/20">
                <div className="text-2xl font-black text-white">15K+</div>
                <div className="text-xs text-purple-200">Happy Clients</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/20">
                <div className="text-2xl font-black text-white">24/7</div>
                <div className="text-xs text-purple-200">Support</div>
              </div>
            </div>
          </div>

          {/* Quick Links - Creative Layout */}
          <div className="lg:col-span-2">
            <h3 className="text-xl font-black text-white mb-6 flex items-center space-x-2">
              <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
              <span>Quick Links</span>
            </h3>
            <ul className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'Services', path: '/services' },
                { name: 'About Us', path: '/about' },
                { name: 'FAQ', path: '/faq' },
                { name: 'Contact', path: '/contact' }
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="group flex items-center space-x-2 text-purple-200 hover:text-white transition-all duration-200"
                  >
                    <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-200" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services - Asymmetric */}
          <div className="lg:col-span-2">
            <h3 className="text-xl font-black text-white mb-6 flex items-center space-x-2">
              <span className="w-2 h-2 bg-pink-400 rounded-full"></span>
              <span>Services</span>
            </h3>
            <ul className="space-y-3">
              {[
                { name: 'Print on Demand', hash: '#print-on-demand' },
                { name: 'Dropshipping', hash: '#dropshipping' },
                { name: 'Product Sourcing', hash: '#product-sourcing' },
                { name: 'Global Warehousing', hash: '#global-warehousing' },
                { name: 'Custom Branding', hash: '#custom-branding' }
              ].map((service) => (
                <li key={service.hash}>
                  <Link 
                    to={`/services${service.hash}`}
                    className="text-purple-200 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - Creative Cards */}
          <div className="lg:col-span-3">
            <h3 className="text-xl font-black text-white mb-6 flex items-center space-x-2">
              <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
              <span>Contact Us</span>
            </h3>
            <ul className="space-y-4">
              <li className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="flex items-start space-x-3">
                  <MapPin size={20} className="text-purple-300 flex-shrink-0 mt-1" />
                  <span className="text-sm text-purple-100">{companyInfo.address}</span>
                </div>
              </li>
              <li className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="flex items-center space-x-3">
                  <Phone size={20} className="text-purple-300 flex-shrink-0" />
                  <a href={`tel:${companyInfo.phone}`} className="text-sm text-purple-100 hover:text-white transition-colors">
                    {companyInfo.phone}
                  </a>
                </div>
              </li>
              <li className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="flex items-center space-x-3">
                  <Mail size={20} className="text-purple-300 flex-shrink-0" />
                  <a href={`mailto:${companyInfo.email}`} className="text-sm text-purple-100 hover:text-white transition-colors break-all">
                    {companyInfo.email}
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar - Creative Design */}
        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-purple-200 text-sm flex items-center space-x-2">
              <span>&copy; {currentYear} {companyInfo.name}. All rights reserved.</span>
            </p>
            <p className="text-purple-200 text-sm flex items-center space-x-2">
              <span>Made with</span>
              <Heart size={16} className="text-pink-400 fill-pink-400 animate-pulse" />
              <span>for entrepreneurs worldwide</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;